import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Button, Select, Radio, Progress, Tag, Space,
    message, Spin, Modal, Input, Alert
} from 'antd';
import {
    CheckCircleFilled, CloseCircleFilled, SendOutlined,
    SaveOutlined, InfoCircleOutlined
} from '@ant-design/icons';
import AYSService from '../../services/AYSService';
import util from '../../utils/util';

// â”€â”€ Facility dropdown for national admins â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function FacilitySelector({ value, onChange }) {
    const [facilities, setFacilities] = React.useState([]);
    React.useEffect(() => {
        AYSService.getFacilities().then(r => {
            setFacilities(r.data?.data || []);
        }).catch(() => {});
    }, []);
    return (
        <Select
            showSearch
            placeholder="Select facility..."
            style={{ width: 260 }}
            value={value || undefined}
            filterOption={(input, opt) => opt.label?.toLowerCase().includes(input.toLowerCase())}
            options={facilities.map(f => ({ value: f.facility_id, label: f.business_name }))}
            onChange={(val, opt) => onChange({ id: val, business_name: opt.label })}
        />
    );
}

const RESPONSES = ['Always', 'Mostly', 'Sometimes', 'Never'];
const SCORE_MAP  = { Always: 4, Mostly: 3, Sometimes: 2, Never: 1 };
const RESP_COLOR = {
    Always:    { bg: '#f0fdf4', border: '#16a34a', text: '#15803d' },
    Mostly:    { bg: '#eff6ff', border: '#2563eb', text: '#1d4ed8' },
    Sometimes: { bg: '#fff7ed', border: '#ea580c', text: '#c2410c' },
    Never:     { bg: '#fef2f2', border: '#dc2626', text: '#b91c1c' },
};

const MONTHS = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December'
];

export default function QoCAYSScorecard() {
    const [facility, setFacility]       = useState(null);
    const [questions, setQuestions]     = useState([]);
    const [assessment, setAssessment]   = useState(null);
    const [editMode, setEditMode]       = useState(false);
    const [responses, setResponses]     = useState({});
    const [loading, setLoading]         = useState(true);
    const [saving, setSaving]           = useState(false);
    const [month, setMonth]             = useState(new Date().getMonth() + 1);
    const [year, setYear]               = useState(new Date().getFullYear());
    const [rejectModal, setRejectModal] = useState(false);
    const [rejectReason, setRejectReason] = useState('');

    const isAdmin    = util.getModules()?.['ays_admin'] === 1;
    const isNational = parseInt(localStorage.getItem('is_national') || '0') === 1 ||
                       parseInt(localStorage.getItem('is_admin') || '0') === 1 || isAdmin;

    useEffect(() => { init(); }, []); // eslint-disable-line

    const init = async () => {
        setLoading(true);
        try {
            const qRes = await AYSService.getQuestions();
            setQuestions(qRes.data?.data || []);
            if (!isNational) {
                // Peer educator â€” load their linked facility automatically
                const fRes = await AYSService.getMyFacility().catch(() => ({ data: { data: null } }));
                setFacility(fRes.data?.data || null);
            }
        } catch(e) {
            message.error(e.message);
        } finally {
            setLoading(false);
        }
    };

    const loadOrCreateAssessment = useCallback(async () => {
        if (!facility) { message.warning('Please select a facility first'); return; }
        setLoading(true);
        try {
            const saveRes = await AYSService.saveAssessment({
                facility_id:      facility.id,
                assessment_month: month,
                assessment_year:  year,
                cadence:          'monthly',
            });
            const aId = saveRes.data?.data?.id;
            if (!aId) throw new Error(saveRes.data?.message || 'Could not create assessment');

            const detailRes = await AYSService.getAssessmentDetail(aId);
            const a = detailRes.data?.data;
            setAssessment(a);

            const rMap = {};
            (a?.responses || []).forEach(r => {
                if (r.response) rMap[r.question_id] = r.response;
            });
            setResponses(rMap);
        } catch(e) {
            message.error(e.message);
        } finally {
            setLoading(false);
        }
    }, [month, year, facility]); // eslint-disable-line

    const handleResponse = (questionId, value) => {
        setResponses(prev => ({ ...prev, [questionId]: value }));
    };

    const saveResponses = async (showMsg = true) => {
        if (!assessment) return;
        setSaving(true);
        try {
            const payload = questions
                .map(q => ({ question_id: q.id, response: responses[q.id] || null }))
                .filter(r => r.response);
            await AYSService.saveResponses(assessment.id, payload);
            if (showMsg) message.success('Responses saved');
            if (editMode) { setEditMode(false); loadOrCreateAssessment(); }
        } catch(e) {
            message.error(e.message);
        } finally {
            setSaving(false);
        }
    };

    const submitAssessment = async () => {
        await saveResponses(false);
        Modal.confirm({
            title:   'Submit Scorecard',
            content: 'Are you sure? You will not be able to edit after submission.',
            okText:  'Submit',
            onOk: async () => {
                util.showLoader();
                try {
                    await AYSService.submitAssessment(assessment.id);
                    message.success('Scorecard submitted successfully');
                    loadOrCreateAssessment();
                } catch(e) {
                    message.error(e.message);
                } finally {
                    util.hideLoader();
                }
            }
        });
    };

    const approveAssessment = async () => {
        util.showLoader();
        try {
            await AYSService.approveAssessment(assessment.id);
            message.success('Assessment approved');
            loadOrCreateAssessment();
        } catch(e) {
            message.error(e.message);
        } finally {
            util.hideLoader();
        }
    };

    const rejectAssessment = async () => {
        util.showLoader();
        try {
            await AYSService.rejectAssessment(assessment.id, rejectReason);
            message.success('Assessment returned for revision');
            setRejectModal(false);
            loadOrCreateAssessment();
        } catch(e) {
            message.error(e.message);
        } finally {
            util.hideLoader();
        }
    };

    const answered  = Object.keys(responses).length;
    const scoreFor = (qid, r) => {
        const base = SCORE_MAP[r] || 0;
        const q = questions.find(x => String(x.id) === String(qid));
        return (q && Number(q.is_reverse) === 1 && base) ? 5 - base : base;
    };
    const total     = questions.length;
    const pct       = total > 0 ? Math.round((answered / total) * 100) : 0;
    const meanScore = answered > 0
        ? Object.entries(responses).reduce((sum, [qid, r]) => sum + scoreFor(qid, r), 0) / answered
        : 0;
    const satisfied = meanScore >= 3.5;

    const sections = questions.reduce((acc, q) => {
        const s = q.section || 'General';
        if (!acc[s]) acc[s] = [];
        acc[s].push(q);
        return acc;
    }, {});

    const isDraft     = !assessment || assessment.status === 'draft';
    const canEditOnce = (assessment?.status === 'submitted' || assessment?.status === 'approved')
        && Number(assessment?.national_edit_used || 0) !== 1;
    const canEdit     = isDraft || (canEditOnce && editMode);
    const isSubmitted = assessment?.status === 'submitted';
    const isApproved  = assessment?.status === 'approved';

    return (
        <div className="page-content">
            <div className="page-head d-flex">
                <div className="my-auto">
                    <h4 style={{ margin: 0 }}>AYS Scorecard</h4>
                    <small className="text-muted">
                        {facility ? facility.business_name : 'Adolescent & Youth Services'}
                    </small>
                </div>
                <div className="my-auto ml-auto">
                    {assessment && (
                        <Space>
                            <Tag color={
                                isApproved  ? 'green' :
                                isSubmitted ? 'blue'  :
                                assessment.status === 'rejected' ? 'red' : 'default'
                            } style={{ fontSize: 13, padding: '4px 10px' }}>
                                {(assessment.status || 'draft').toUpperCase()}
                            </Tag>
                            {canEditOnce && !editMode && (
                                <Button size="small" onClick={() => setEditMode(true)}>
                                    Edit (one-time)
                                </Button>
                            )}
                        </Space>
                    )}
                </div>
            </div>

            <div className="page-pad">
                {loading ? <Spin /> : (
                    <>
                        {/* Period + facility selector */}
                        {!assessment && (
                            <Card style={{ marginBottom: 16 }}>
                                <Space wrap>
                                    {isNational && (
                                        <FacilitySelector
                                            value={facility?.id}
                                            onChange={f => setFacility(f)}
                                        />
                                    )}
                                    <span style={{ fontWeight: 600 }}>Period:</span>
                                    <Select
                                        value={month}
                                        onChange={setMonth}
                                        style={{ width: 140 }}
                                        options={MONTHS.map((m, i) => ({ value: i+1, label: m }))}
                                    />
                                    <Select
                                        value={year}
                                        onChange={setYear}
                                        style={{ width: 100 }}
                                        options={[2024,2025,2026,2027].map(y => ({ value: y, label: String(y) }))}
                                    />
                                    <Button type="primary"
                                        disabled={isNational && !facility}
                                        onClick={loadOrCreateAssessment}>
                                        Open Scorecard
                                    </Button>
                                </Space>
                            </Card>
                        )}

                        {assessment && (
                            <>
                                {assessment.status === 'rejected' && (
                                    <Alert type="error" showIcon
                                        message="Scorecard returned for revision"
                                        description={assessment.notes}
                                        style={{ marginBottom: 16 }}
                                    />
                                )}
                                {isApproved && (
                                    <Alert type="success" showIcon
                                        message="Scorecard approved"
                                        style={{ marginBottom: 16 }}
                                    />
                                )}

                                {/* Progress */}
                                <Card style={{ marginBottom: 16 }}>
                                    <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', alignItems: 'center' }}>
                                        <div>
                                            <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Progress</div>
                                            <Progress percent={pct} style={{ width: 180 }}
                                                strokeColor={pct === 100 ? '#16a34a' : '#1890ff'} />
                                            <div style={{ fontSize: 12, color: '#888' }}>{answered} of {total} answered</div>
                                        </div>
                                        {answered > 0 && (
                                            <div style={{ textAlign: 'center' }}>
                                                <div style={{ fontSize: 28, fontWeight: 800, color: satisfied ? '#16a34a' : '#dc2626' }}>
                                                    {meanScore.toFixed(2)}
                                                </div>
                                                <div style={{ fontSize: 12, color: '#888' }}>Mean score</div>
                                                <Tag color={satisfied ? 'green' : 'red'} style={{ marginTop: 4 }}>
                                                    {satisfied
                                                        ? <><CheckCircleFilled /> Satisfied</>
                                                        : <><CloseCircleFilled /> Not Satisfied</>}
                                                </Tag>
                                            </div>
                                        )}
                                        <div style={{ marginLeft: 'auto' }}>
                                            <Space wrap>
                                                <span style={{ fontSize: 11, color: '#aaa' }}>
                                                    {MONTHS[month-1]} {year} · {facility?.business_name}
                                                </span>
                                                {isDraft && (
                                                    <>
                                                        <Button icon={<SaveOutlined />} loading={saving}
                                                            onClick={() => saveResponses()}>Save draft</Button>
                                                        <Button type="primary" icon={<SendOutlined />}
                                                            disabled={answered === 0}
                                                            onClick={submitAssessment}>Submit</Button>
                                                    </>
                                                )}
                                                {isSubmitted && isAdmin && (
                                                    <>
                                                        <Button type="primary"
                                                            style={{ background: '#16a34a', borderColor: '#16a34a' }}
                                                            onClick={approveAssessment}>Approve</Button>
                                                        <Button danger onClick={() => setRejectModal(true)}>Return</Button>
                                                    </>
                                                )}
                                            </Space>
                                        </div>
                                    </div>
                                </Card>

                                {/* Scoring key */}
                                <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                                    {RESPONSES.map(r => (
                                        <span key={r} style={{
                                            padding: '3px 10px', borderRadius: 4, fontSize: 12, fontWeight: 600,
                                            background: RESP_COLOR[r].bg,
                                            border: `1px solid ${RESP_COLOR[r].border}`,
                                            color: RESP_COLOR[r].text,
                                        }}>
                                            {r} = {SCORE_MAP[r]}
                                        </span>
                                    ))}
                                    <span style={{ fontSize: 11, color: '#888', marginLeft: 8, alignSelf: 'center' }}>
                                        <InfoCircleOutlined /> Mean ≥3.5 = Satisfied
                                    </span>
                                </div>

                                {/* Questions */}
                                {Object.entries(sections).map(([section, qs]) => (
                                    <div key={section} style={{ marginBottom: 24 }}>
                                        <div style={{
                                            fontWeight: 700, fontSize: 14, color: '#1e40af',
                                            borderLeft: '3px solid #1e40af', paddingLeft: 10, marginBottom: 12
                                        }}>
                                            {section}
                                            {section === 'Key Population' && (
                                                <Tag color="purple" style={{ marginLeft: 8, fontSize: 10 }}>KP only</Tag>
                                            )}
                                        </div>
                                        {qs.map((q, qi) => {
                                            const resp  = responses[q.id];
                                            const color = resp ? RESP_COLOR[resp] : null;
                                            return (
                                                <Card key={q.id} size="small" style={{
                                                    marginBottom: 8,
                                                    borderLeft: resp ? `3px solid ${color.border}` : '3px solid #e5e7eb',
                                                    background: resp ? color.bg : '#fafafa',
                                                }}>
                                                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                                                        <span style={{ fontWeight: 700, color: '#6b7280', minWidth: 24, paddingTop: 2 }}>
                                                            {qi + 1}.
                                                        </span>
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{ marginBottom: 10, lineHeight: 1.5 }}>{q.question_text}</div>
                                                            <Radio.Group
                                                                value={resp || null}
                                                                onChange={e => handleResponse(q.id, e.target.value)}
                                                                disabled={!canEdit}
                                                            >
                                                                {RESPONSES.map(r => (
                                                                    <Radio.Button key={r} value={r} style={{
                                                                        marginRight: 4,
                                                                        ...(resp === r ? {
                                                                            background:  color.bg,
                                                                            borderColor: color.border,
                                                                            color:       color.text,
                                                                            fontWeight:  700,
                                                                        } : {})
                                                                    }}>
                                                                        {r}
                                                                    </Radio.Button>
                                                                ))}
                                                            </Radio.Group>
                                                        </div>
                                                        {resp && (
                                                            <Tag style={{
                                                                color: color.text, background: color.bg,
                                                                borderColor: color.border, fontWeight: 700, fontSize: 13
                                                            }}>
                                                                {scoreFor(q.id, resp)}
                                                            </Tag>
                                                        )}
                                                    </div>
                                                </Card>
                                            );
                                        })}
                                    </div>
                                ))}

                                {canEditOnce && editMode && (
                                    <Alert type="warning" showIcon style={{ marginTop: 16 }}
                                        message="This is your one-time edit"
                                        description="This scorecard was already submitted. As a national office user you may correct it, but only this once -- once you save, it locks again and cannot be edited further." />
                                )}
                                {(isDraft || canEditOnce) && (
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                                        {isDraft && (
                                            <Button icon={<SaveOutlined />} loading={saving} onClick={() => saveResponses()}>
                                                Save draft
                                            </Button>
                                        )}
                                        {isDraft && (
                                            <Button type="primary" icon={<SendOutlined />}
                                                disabled={answered === 0} onClick={submitAssessment}>
                                                Submit Scorecard
                                            </Button>
                                        )}
                                        {canEditOnce && editMode && (
                                            <Button type="primary" icon={<SaveOutlined />} loading={saving}
                                                onClick={() => saveResponses()}>
                                                Save correction (uses your one-time edit)
                                            </Button>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>

            <Modal
                title="Return Scorecard for Revision"
                open={rejectModal}
                onCancel={() => setRejectModal(false)}
                onOk={rejectAssessment}
                okText="Return"
                okButtonProps={{ danger: true }}
            >
                <Input.TextArea rows={3}
                    placeholder="Reason for returning..."
                    value={rejectReason}
                    onChange={e => setRejectReason(e.target.value)}
                />
            </Modal>
        </div>
    );
}


